import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { usePageTitleSlot } from './page-title-slot';

/**
 * Standard title row for a page — except the title/subtitle no longer
 * render here. They publish into the fixed `AppHeader` (opposite the brand
 * mark) via `usePageTitleSlot`, so every page that renders this component
 * gets a consistent header-mounted title for free. This component itself
 * renders only `children` (action buttons), right-aligned, and renders
 * nothing at all when there are none.
 *
 *   <PageHeader title="Customers" subtitle="The accounts we work with.">
 *     <Button>Add customer</Button>
 *   </PageHeader>
 *
 * For pages with a rich, non-title header (today: timesheets — avatar +
 * delegation chrome + week pager), skip `<PageHeader>` and use
 * `<PageShellHeaderSlot>` to keep the standard spacing without the
 * title+subtitle assumption.
 */
export function PageHeader({
  title,
  titleAction,
  subtitle,
  count,
  children,
  className,
}: {
  title: ReactNode;
  /**
   * A control that belongs to the TITLE rather than to the page — a pencil that
   * renames what the title names. Rendered as a sibling of the title text in
   * the header slot, never inside the `<h1>` (an element inside the heading
   * would contribute to its accessible name).
   */
  titleAction?: ReactNode;
  subtitle?: ReactNode;
  /**
   * Optional record count shown next to the title in a smaller, muted
   * weight, parenthesized (e.g. "Customers (20)").
   */
  count?: number;
  children?: ReactNode;
  className?: string;
}) {
  usePageTitleSlot(
    <div className="flex min-w-0 flex-col justify-center">
      <div className={cn('flex min-w-0 items-center gap-1.5')}>
        <h1 className="truncate text-lg leading-tight font-semibold">
          {title}
          {count != null ? (
            <span className="ml-1.5 align-baseline text-xs font-normal text-muted-foreground num">
              ({count})
            </span>
          ) : null}
        </h1>
        {titleAction}
      </div>
      {subtitle ? (
        <p className="truncate text-xs leading-tight text-muted-foreground">{subtitle}</p>
      ) : null}
    </div>,
  );

  if (!children) return null;

  return (
    <header className={cn('flex flex-wrap items-center justify-end gap-3', className)}>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </header>
  );
}

/**
 * Escape hatch for pages whose header isn't just title + subtitle (today:
 * timesheets). Sits inside `<PageShell>` and provides the same `<header>`
 * element + flex-wrap behavior — the contents are caller-defined. Using
 * this instead of a bare `<header>` keeps the semantic tag consistent
 * across pages and makes it grep-able when we want to audit every page
 * header at once.
 */
export function PageShellHeaderSlot({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('flex flex-wrap items-center justify-between gap-3', className)}>
      {children}
    </header>
  );
}
